import React, {useLayoutEffect, useRef} from "react";
import {useFrame} from "@react-three/fiber";
import {applyAxesToObject} from "./orientationAxes";
import {getCubeOrigin} from "./snapPlacement";

const resolveAxesLength = ({detailData, sizeM}) => {
    if (sizeM) {
        const {width = 0.2, height = 0.2, depth = 0.2} = sizeM;
        return Math.min(0.22, Math.max(0.07, Math.max(width, height, depth) * 0.35));
    }
    const lengthMm = Math.max(Number(detailData?.length) || 200, 1);
    const widthMm = Math.max(Number(detailData?.width) || 200, 1);
    return Math.min(0.22, Math.max(0.07, Math.min(lengthMm, widthMm) * 0.001 * 0.35));
};

/**
 * RGB-стрелки осей (AxesHelper) в углу объёма.
 * detailData — деталь/панель, origin (0,0,0).
 * sizeM — модуль на сцене: длина стрелок от габаритов.
 * anchorAtModuleCorner — (0,0,0) = точка привязки модуля; иначе getCubeOrigin(sizeM).
 * worldAlignedInParent — компенсировать поворот родителя (оси = мировые X/Y/Z).
 */
const DetailPanelAxes = ({
    detailData,
    sizeM = null,
    anchorAtModuleCorner = false,
    worldAlignedInParent = false,
    axes = null,
    faceFlip = false,
    orientationSourceRef = null,
    dimmed = false,
}) => {
    const groupRef = useRef(null);
    const axesRef = useRef(null);
    const originalColorsRef = useRef(null);
    const len = resolveAxesLength({detailData, sizeM});
    const cornerOrigin = anchorAtModuleCorner || !sizeM
        ? {x: 0, y: 0, z: 0}
        : getCubeOrigin(sizeM);

    useLayoutEffect(() => {
        const root = groupRef.current;
        if (!root) return;
        root.position.set(cornerOrigin.x, cornerOrigin.y, cornerOrigin.z);
        if (worldAlignedInParent) return;
        if (orientationSourceRef?.current) {
            const src = orientationSourceRef.current;
            root.quaternion.copy(src.quaternion);
            root.scale.copy(src.scale);
            return;
        }
        if (axes) {
            applyAxesToObject(root, axes, faceFlip);
            return;
        }
        root.quaternion.identity();
        root.scale.set(1, 1, 1);
    }, [
        axes,
        faceFlip,
        cornerOrigin.x,
        cornerOrigin.y,
        cornerOrigin.z,
        worldAlignedInParent,
        orientationSourceRef,
    ]);

    useFrame(() => {
        const root = groupRef.current;
        if (!root) return;
        if (!worldAlignedInParent && orientationSourceRef?.current) {
            const src = orientationSourceRef.current;
            root.quaternion.copy(src.quaternion);
            root.scale.copy(src.scale);
            return;
        }
        if (axes && !worldAlignedInParent) {
            applyAxesToObject(root, axes, faceFlip);
            return;
        }
        if (!worldAlignedInParent) return;
        const parent = root?.parent;
        if (!parent) return;
        root.quaternion.copy(parent.quaternion).invert();
        root.scale.set(1, 1, 1);
    });

    useLayoutEffect(() => {
        const root = axesRef.current;
        if (!root) return undefined;

        if (originalColorsRef.current == null) {
            const originals = [];
            root.traverse((obj) => {
                if (obj?.material?.color) {
                    originals.push({obj, color: obj.material.color.clone()});
                }
            });
            originalColorsRef.current = originals;
        }

        const helperOpacity = dimmed ? 0.35 : 1;
        root.traverse((obj) => {
            if (!obj?.material) return;
            obj.material.depthTest = false;
            obj.material.depthWrite = false;
            if (obj.material.opacity != null || dimmed) {
                obj.material.transparent = helperOpacity < 1;
                obj.material.opacity = helperOpacity;
            }
            if (obj.material.color) {
                if (dimmed) {
                    obj.material.color.set("#BDBDBD");
                } else if (originalColorsRef.current) {
                    const entry = originalColorsRef.current.find((x) => x.obj === obj);
                    if (entry?.color) obj.material.color.copy(entry.color);
                }
            }
        });

        return undefined;
    }, [dimmed, len]);

    return (
        <group ref={groupRef} userData={{module3dRole: "helper"}}>
            <axesHelper ref={axesRef} args={[len]} raycast={() => null} renderOrder={10}/>
        </group>
    );
};

export default DetailPanelAxes;
